import {
  type DefaultOptions,
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryClientConfig,
} from "@tanstack/react-query";
import { isUnauthorizedError } from "./types";
import { reportToSentry } from "../monitoring/sentry";

export const defaultQueryOptions: DefaultOptions = {
  queries: {
    staleTime: 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: (failureCount, error) => {
      if (isUnauthorizedError(error)) {
        return false;
      }

      return failureCount < 2;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: false,
  },
  mutations: {
    retry: 0,
  },
};

export const createQueryClient = (config?: QueryClientConfig) => {
  const mergedDefaultOptions: QueryClientConfig["defaultOptions"] = {
    queries: {
      ...defaultQueryOptions.queries,
      ...config?.defaultOptions?.queries,
    },
    mutations: {
      ...defaultQueryOptions.mutations,
      ...config?.defaultOptions?.mutations,
    },
  };

  const queryCache =
    config?.queryCache ??
    new QueryCache({
      onError: (error, query) => {
        reportToSentry(error, {
          mechanism: "react-query",
          handled: true,
          tags: {
            react_query_source: "query",
          },
          extra: {
            queryHash: query.queryHash,
            queryKey: query.queryKey,
            meta: query.meta,
          },
        });
      },
    });

  const mutationCache =
    config?.mutationCache ??
    new MutationCache({
      onError: (error, variables, _context, mutation) => {
        reportToSentry(error, {
          mechanism: "react-query",
          handled: true,
          tags: {
            react_query_source: "mutation",
          },
          extra: {
            mutationKey: mutation.options.mutationKey,
            meta: mutation.meta,
            variables,
          },
        });
      },
    });

  return new QueryClient({
    ...config,
    queryCache,
    mutationCache,
    defaultOptions: mergedDefaultOptions,
  });
};
